import React from "react";
import { AudioLinesIcon, LockIcon } from "lucide-react";
import isEpisodeLocked from "../isEpisodeLocked";
import type { GetShowQuery } from "@/codegen/graphql/generated/graphql";
import { cn } from "@/lib/utils";
import usePremium from "@/hooks/usePremium";

export interface EpisodesListProps extends React.ComponentProps<"div"> {
  episodes: GetShowQuery["show"]["episodes"];
  activeEpisodeIndex: number;
  onEpisodeSelect: (
    episode: GetShowQuery["show"]["episodes"][number],
    index: number,
  ) => void;
}

export const EpisodeItem: React.FC<{
  episode: GetShowQuery["show"]["episodes"][number];
  isActive: boolean;
  onClick: () => void;
}> = ({ episode, isActive, onClick }) => {
  const { isPremiumUser } = usePremium();
  const locked = isEpisodeLocked(episode, isPremiumUser);
  return (
    <button
      data-test-id={`player-episode-item-${episode.id}`}
      className={cn(
        "group bg-neutral-60 font-bol hover:border-primary relative flex h-14 w-full cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-sm border border-neutral-50 px-4 text-center text-lg",
        isActive && !locked && "bg-primary border-primary",
      )}
      onClick={onClick}
    >
      {locked && (
        <div className="text-neutral-60 group-hover:bg-primary absolute top-0 right-0 rounded-bl-sm bg-neutral-50 p-1 group-hover:text-white">
          <LockIcon className="size-3" />
        </div>
      )}
      {isActive && !locked ? (
        <AudioLinesIcon className="size-8" />
      ) : (
        <p className="grow">{episode.number ? episode.number : "Trailer"}</p>
      )}
    </button>
  );
};

const EpisodesList: React.FC<EpisodesListProps> = ({
  className,
  episodes,
  activeEpisodeIndex,
  onEpisodeSelect,
  ...props
}) => (
  <div className={cn("grid grid-cols-5 gap-2", className)} {...props}>
    {episodes.map((episode, index) => (
      <EpisodeItem
        key={episode.id}
        episode={episode}
        isActive={index === activeEpisodeIndex}
        onClick={() => onEpisodeSelect(episode, index)}
      />
    ))}
  </div>
);

export default EpisodesList;
